/**
 * Combos y recetas (spec 20): definición que configura la dueña, lo que pide la venta y lo que devuelve el sistema.
 * El cálculo de las cantidades vive en `../combos.ts` (una sola función para la pantalla y el servidor).
 */
import { z } from 'zod';
import { decimalNoNegativo, decimalPositivo, decimalString, paginacion, uuid } from './comun.ts';

export const TIPOS_COMBO = ['fijo', 'por_medida'] as const;
export type TipoCombo = (typeof TIPOS_COMBO)[number];
export const TIPO_COMBO_ETIQUETA: Record<TipoCombo, string> = { fijo: 'Combo (cantidades fijas)', por_medida: 'Receta por medida' };

export const UNIDADES_MEDIDA_COMBO = ['m2', 'ml', 'm3', 'unidad'] as const;
export type UnidadMedidaCombo = (typeof UNIDADES_MEDIDA_COMBO)[number];
/** Cómo se escribe la unidad de la medida del trabajo. */
export const UNIDAD_MEDIDA_ETIQUETA: Record<UnidadMedidaCombo, string> = { m2: 'm²', ml: 'm lineales', m3: 'm³', unidad: 'unidades' };

export const MODOS_COMPONENTE = ['fijo', 'consumo', 'rendimiento', 'derivado'] as const;
export type ModoComponente = (typeof MODOS_COMPONENTE)[number];
export const MODO_COMPONENTE_ETIQUETA: Record<ModoComponente, string> = {
  fijo: 'Cantidad fija',
  consumo: 'Consumo por unidad de medida',
  rendimiento: 'Rendimiento (lo que cubre cada uno)',
  derivado: 'Derivado de otro componente',
};

export const REDONDEOS_COMPONENTE = ['entero', 'fraccion'] as const;
export type RedondeoComponente = (typeof REDONDEOS_COMPONENTE)[number];
export const REDONDEO_COMPONENTE_ETIQUETA: Record<RedondeoComponente, string> = {
  entero: 'Hacia arriba al entero',
  fraccion: 'Hacia arriba a la fracción mínima del producto',
};

const porcentaje = decimalNoNegativo.refine((v) => Number(v) <= 100, 'El descuento no puede superar 100 %');

export const empaqueComboInput = z.object({
  producto_id: uuid,
  presentacion_id: uuid,
  /** Lo que trae este empaque, en la misma unidad que el factor del componente (p. ej. 76 m o 5,6 kg). */
  contenido: decimalPositivo,
});
export type EmpaqueComboInput = z.infer<typeof empaqueComboInput>;

export const componenteComboInput = z.object({
  producto_id: uuid,
  presentacion_id: uuid,
  modo: z.enum(MODOS_COMPONENTE),
  /** fijo: cantidad · consumo: cuánto se gasta por unidad de medida · rendimiento: cuánto cubre cada unidad. Vacío en derivado. */
  factor: decimalPositivo.nullable().default(null),
  /** Lo que trae la unidad que se vende, en la unidad del factor (caja de 100 tornillos, cuñete de 28 kg). */
  contenido: decimalPositivo.default('1'),
  /** derivado: número (1, 2, 3…) del componente base dentro del combo. */
  base_orden: z.number().int().min(1).nullable().default(null),
  /** derivado: ajuste % sobre la necesidad del base sin redondear (−10 = 10 % menos). */
  ajuste_pct: decimalString.refine((v) => Number(v) > -100, 'El ajuste tiene que ser mayor que −100 %').refine((v) => Number(v) <= 1000, 'El ajuste no puede pasar de 1.000 %').nullable().default(null),
  redondeo: z.enum(REDONDEOS_COMPONENTE).default('entero'),
  /** Descuento propio del componente cuando se vende en el combo; vacío = el del combo. */
  descuento_pct: porcentaje.nullable().default(null),
  /** Otros empaques del mismo material para completar la necesidad (mastique, cinta). */
  empaques: z.array(empaqueComboInput).max(5).default([]),
});
export type ComponenteComboInput = z.infer<typeof componenteComboInput>;

export const comboInput = z
  .object({
    codigo: z.string().trim().min(1, 'El código es obligatorio').max(40).toUpperCase(),
    nombre: z.string().trim().min(2, 'Mínimo 2 caracteres').max(120),
    descripcion: z.string().trim().max(1000).default(''),
    tipo: z.enum(TIPOS_COMBO),
    unidad_medida: z.enum(UNIDADES_MEDIDA_COMBO).nullable().default(null),
    /** Descuento para todos los componentes que no traen el suyo. */
    descuento_pct: porcentaje.default('0'),
    componentes: z.array(componenteComboInput).min(1, 'Agrega al menos un producto').max(40),
  })
  .superRefine((c, ctx) => {
    if (c.tipo === 'por_medida' && !c.unidad_medida) ctx.addIssue({ code: 'custom', path: ['unidad_medida'], message: 'Elige la unidad de la medida (m², m lineales, m³ o unidades)' });
    if (c.tipo === 'fijo' && c.unidad_medida) ctx.addIssue({ code: 'custom', path: ['unidad_medida'], message: 'Un combo de cantidades fijas no lleva unidad de medida' });
    const productos = new Set<string>();
    c.componentes.forEach((k, i) => {
      const ruta = ['componentes', i];
      for (const p of [k.producto_id, ...k.empaques.map((e) => e.producto_id)]) {
        if (productos.has(p)) ctx.addIssue({ code: 'custom', path: ruta, message: 'Un producto no puede estar dos veces en el mismo combo (tampoco como empaque de otro)' });
        productos.add(p);
      }
      if (c.tipo === 'fijo' && (k.modo === 'consumo' || k.modo === 'rendimiento')) ctx.addIssue({ code: 'custom', path: [...ruta, 'modo'], message: 'Un combo de cantidades fijas no se calcula por medida: usa «Cantidad fija»' });
      if (k.modo === 'derivado') {
        if (k.factor !== null) ctx.addIssue({ code: 'custom', path: [...ruta, 'factor'], message: 'El derivado no lleva factor: sale del componente base' });
        if (k.base_orden === null || k.ajuste_pct === null) ctx.addIssue({ code: 'custom', path: [...ruta, 'base_orden'], message: 'Indica de qué componente sale y el ajuste %' });
        else {
          const base = c.componentes[k.base_orden - 1];
          if (!base || k.base_orden === i + 1) ctx.addIssue({ code: 'custom', path: [...ruta, 'base_orden'], message: 'El componente base tiene que ser otro componente del combo' });
          else if (base.modo === 'derivado') ctx.addIssue({ code: 'custom', path: [...ruta, 'base_orden'], message: 'El componente base no puede ser otro derivado' });
        }
      } else {
        if (k.factor === null) ctx.addIssue({ code: 'custom', path: [...ruta, 'factor'], message: 'Escribe el factor (la cantidad, el consumo o el rendimiento)' });
        if (k.base_orden !== null || k.ajuste_pct !== null) ctx.addIssue({ code: 'custom', path: [...ruta, 'base_orden'], message: 'Solo el derivado sale de otro componente' });
      }
    });
  });
export type ComboInput = z.infer<typeof comboInput>;
export type ComboEntrada = z.input<typeof comboInput>;

export const combosFiltros = paginacion.extend({
  q: z.string().trim().max(120).optional(),
  activo: z
    .enum(['true', 'false', 'todos'])
    .default('true')
    .transform((v) => (v === 'todos' ? undefined : v === 'true')),
});
export type CombosFiltros = z.infer<typeof combosFiltros>;

/** «Duplicar»: la copia lleva otro código y otro nombre (una receta por color). */
export const duplicarComboInput = z.object({
  codigo: z.string().trim().min(1, 'El código es obligatorio').max(40).toUpperCase(),
  nombre: z.string().trim().min(2, 'Mínimo 2 caracteres').max(120),
});

/** Vista previa de un combo guardado: la medida del trabajo (o el número de combos). */
export const calcularComboInput = z.object({ medida: decimalPositivo });

/** Un combo dentro de una venta, una cotización o una espera: su grupo (los renglones lo citan) y la medida. */
export const comboEnVentaInput = z.object({
  grupo: uuid,
  combo_id: uuid,
  /** Receta por medida: la medida del trabajo · combo fijo: el número de combos (entero). */
  medida: decimalPositivo,
});
export type ComboEnVentaInput = z.infer<typeof comboEnVentaInput>;

// ─── Salidas ────────────────────────────────────────────────────────────────
export interface ComboEmpaqueDTO {
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  presentacion_nombre: string;
  contenido: string;
}

export interface ComboComponenteDTO {
  orden: number;
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  producto_activo: boolean;
  presentacion_nombre: string;
  unidades_presentacion: string;
  fraccion_minima: string;
  modo: ModoComponente;
  factor: string | null;
  contenido: string;
  base_orden: number | null;
  ajuste_pct: string | null;
  redondeo: RedondeoComponente;
  descuento_pct: string | null;
  empaques: ComboEmpaqueDTO[];
}

export interface ComboDTO {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string;
  tipo: TipoCombo;
  unidad_medida: UnidadMedidaCombo | null;
  descuento_pct: string;
  activo: boolean;
  version: number;
  /** Todos sus productos están activos: se puede vender. */
  vendible: boolean;
  componentes: ComboComponenteDTO[];
  creado_por_nombre: string;
  creado_en: string;
  actualizado_en: string | null;
}

export interface ComboResumenDTO {
  id: string;
  codigo: string;
  nombre: string;
  tipo: TipoCombo;
  unidad_medida: UnidadMedidaCombo | null;
  descuento_pct: string;
  activo: boolean;
  vendible: boolean;
  componentes: number;
}

/** Un renglón calculado de un combo: qué producto, cuánto y de dónde salió la cuenta. */
export interface RenglonComboDTO {
  /** Número del componente dentro del combo. */
  orden: number;
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  /** Lo que hace falta en la unidad del factor (piezas, kg, metros…), con 8 decimales. */
  necesidad: string;
  /** Cantidad antes de redondear (necesidad ÷ contenido), con 8 decimales. */
  sin_redondear: string;
  /** Cantidad que va al renglón (presentaciones). */
  cantidad: string;
  /** Descuento del combo para este renglón. */
  descuento_pct: string;
  /** El renglón es un empaque alternativo del componente. */
  empaque: boolean;
  /** La cuenta en palabras: «26 × 0,36 = 9,36 → 10». */
  explicacion: string;
}

export interface CalculoComboDTO {
  combo_id: string;
  codigo: string;
  nombre: string;
  tipo: TipoCombo;
  unidad_medida: UnidadMedidaCombo | null;
  version: number;
  medida: string;
  renglones: RenglonComboDTO[];
}

/** Un combo tal como quedó en una venta, una cotización o un documento emitido. */
export interface ComboEnDocumentoDTO {
  grupo: string;
  combo_id: string;
  combo_version: number;
  codigo: string;
  nombre: string;
  tipo: TipoCombo;
  unidad_medida: UnidadMedidaCombo | null;
  medida: string;
}
